import { useState } from 'react';
import type { IntegrationEventDto, IntegrationStatusDto, ReconcileResultDto, SyncResultDto } from '@shared/types';
import { localDateTime, timeAgo } from '@shared/format';
import { errorMessage, useApp, useLoad } from '../context';
import { Card, Empty, ErrorBox, Loading, Pill, TopBar } from '../components/ui';

const KIND: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'muted'> = { Awarded: 'ok', Duplicate: 'muted', Pending: 'info', UnknownEmployee: 'warn', Failed: 'bad', NoActiveRule: 'muted', CappedDaily: 'muted', CappedWeekly: 'muted', Anonymous: 'muted', Ignored: 'muted' };

export function Integration() {
  const { api, toast } = useApp();
  const status = useLoad(() => api.get<IntegrationStatusDto>('/api/integration/connecteam/status'), [api]);
  const [filter, setFilter] = useState('');
  const events = useLoad(() => api.get<IntegrationEventDto[]>(`/api/integration/connecteam/events?take=100${filter ? `&status=${filter}` : ''}`), [api, filter]);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (name: string, fn: () => Promise<string>) => {
    setBusy(name);
    try { toast(await fn()); status.reload(); events.reload(); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(null); }
  };

  return (
    <div className="page">
      <TopBar title="Connecteam Integration" back="/more" />
      {status.loading && !status.data && <Loading />}
      {status.error && <ErrorBox message={status.error} onRetry={status.reload} />}
      {status.data && (
        <Card>
          <div className="kv"><span>Status</span><span>{status.data.enabled ? <Pill kind="ok">Enabled</Pill> : <Pill kind="muted">Disabled (Connecteam:Enabled=false)</Pill>}</span></div>
          <div className="kv"><span>Last employee sync</span><span title={localDateTime(status.data.lastEmployeeSyncUtc)}>{timeAgo(status.data.lastEmployeeSyncUtc)}</span></div>
          <div className="kv"><span>Last reconciliation</span><span title={localDateTime(status.data.lastReconcileUtc)}>{timeAgo(status.data.lastReconcileUtc)}</span></div>
          <div className="kv"><span>Pending</span><span className="tabular">{status.data.pendingEvents}</span></div>
          <div className="kv"><span>Held — unknown employee</span><span className="tabular">{status.data.unknownEmployeeEvents}</span></div>
          <div className="kv"><span>Failed</span><span className="tabular">{status.data.failedEvents}</span></div>
          <div className="row-gap mt">
            <button className="btn btn-secondary" disabled={!!busy} onClick={() => run('sync', async () => {
              const r = await api.post<SyncResultDto>('/api/integration/connecteam/sync-employees');
              return `Employees synced: ${r.created} new, ${r.updated} updated, ${r.deactivated} deactivated`;
            })}>{busy === 'sync' ? 'Syncing…' : 'Sync employees'}</button>
            <button className="btn btn-secondary" disabled={!!busy} onClick={() => run('rec', async () => {
              const r = await api.post<ReconcileResultDto>('/api/integration/connecteam/reconcile');
              return `Reconciled ${r.submissionsSeen} submissions: ${r.awarded} awarded, ${r.duplicates} already counted`;
            })}>{busy === 'rec' ? 'Reconciling…' : 'Reconcile submissions'}</button>
          </div>
        </Card>
      )}
      <div className="filter-row">
        {['', 'Awarded', 'UnknownEmployee', 'Failed', 'Duplicate'].map((f) => (
          <button key={f || 'all'} className={`chip ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>{f || 'All'}</button>
        ))}
      </div>
      {events.data && (
        <Card>
          {events.data.length === 0 ? <Empty>No inbound events.</Empty> : events.data.map((e) => (
            <div key={e.integrationEventId} className="event-row">
              <div className="row-between"><code>{e.eventType}</code><Pill kind={KIND[e.status] ?? 'muted'}>{e.status}</Pill></div>
              <div className="muted small">{e.channel} • {e.externalId ?? '—'} • {localDateTime(e.receivedAtUtc)} • attempts {e.attempts}</div>
              {e.lastError && <div className="warn small">{e.lastError}</div>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
